import { Box, Stack, Typography } from '@mui/material'
import { DashboardSnapshotDto } from '../../interfaces/IDashboard'
import { usePolling } from '../../hooks/usePolling'
import OpenByTypeCard from './components/OpenByTypeCard'
import OpenSingleCard from './components/OpenSingleCard'
import KpiBigNumber from './components/KpiBigNumber'
import UpcomingPlannedCard from './components/UpcomingPlannedCard'
import ActiveWorkCard from './components/ActiveWorkCard'
import ActiveIncidentsCard from './components/ActiveIncidentsCard'
import PlannedDeviationsCard from './components/PlannedDeviationsCard'
import { ALERT_WARN_COLOR, OVERDUE_WARN_COLOR } from './components/BucketColors'
import { INCIDENT_BUCKETS, INCIDENT_BUCKET_LABELS, PLANNED_BUCKETS } from './components/Buckets'

function DashboardWallboard() {
  const snapshot = usePolling<DashboardSnapshotDto>('/api/stats/dashboard/snapshot', 30_000)

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 3, md: 4, xl: 5 },
        gap: { xs: 2, sm: 3, md: 4 },
        bgcolor: 'background.default',
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        sx={{
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'baseline' },
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 'clamp(2rem, 5vw, 5.5rem)', fontWeight: 700, lineHeight: 1 }}>
          Driftshendelser
        </Typography>
        {snapshot.lastUpdatedAt && (
          <Typography
            sx={{ fontSize: 'clamp(0.875rem, 1.2vw, 1.5rem)' }}
            color="text.secondary"
          >
            Sist oppdatert {snapshot.lastUpdatedAt.toLocaleTimeString('nb-NO')}
          </Typography>
        )}
      </Stack>

      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '2fr 2fr 1fr 1fr' },
          gap: { xs: 2, md: 3, xl: 4 },
        }}
      >
        <OpenByTypeCard
          title="Fellesfeil"
          buckets={snapshot.data?.openIncidentByCriticality ?? null}
          bucketOrder={INCIDENT_BUCKETS}
          bucketLabels={INCIDENT_BUCKET_LABELS}
          variant="wall"
        />
        <OpenByTypeCard
          title="Planlagt arbeid"
          buckets={snapshot.data?.openPlannedByCriticality ?? null}
          bucketOrder={PLANNED_BUCKETS}
          variant="wall"
        />
        <OpenSingleCard
          title="Generell hendelse"
          value={snapshot.data?.openGenericTotal ?? null}
          variant="wall"
        />
        <KpiBigNumber
          label="Totalt åpne"
          value={snapshot.data?.totalOpen ?? null}
          variant="wall"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: { xs: 2, md: 3 },
        }}
      >
        <KpiBigNumber
          label="Fellesfeil uten varsel"
          value={snapshot.data?.openIncidentWithoutAlert ?? null}
          variant="wall"
          accentColor={(snapshot.data?.openIncidentWithoutAlert ?? 0) > 0 ? ALERT_WARN_COLOR : undefined}
        />
        <KpiBigNumber
          label="Forfalt sluttid"
          value={snapshot.data?.overdueOpen ?? null}
          variant="wall"
          accentColor={(snapshot.data?.overdueOpen ?? 0) > 0 ? OVERDUE_WARN_COLOR : undefined}
        />
        <KpiBigNumber
          label="Berørte kunder"
          value={snapshot.data?.uniqueAffectedCustomers ?? null}
          variant="wall"
        />
        <KpiBigNumber
          label="Berørte leveransepunkter"
          value={snapshot.data?.uniqueAffectedServicePoints ?? null}
          variant="wall"
        />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: { xs: 2, md: 3, xl: 4 },
        }}
      >
        <ActiveIncidentsCard variant="wall" />
        <UpcomingPlannedCard variant="wall" />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: { xs: 2, md: 3, xl: 4 },
        }}
      >
        <PlannedDeviationsCard variant="wall" />
        <ActiveWorkCard variant="wall" />
      </Box>
    </Box>
  )
}

export default DashboardWallboard
